import { maxBreakpoints, minBreakpoints, theme } from '@noths/polaris-client-ribbons-base';
import { applyKeyframe, mediaQuery, pxToRem } from '@noths/polaris-client-styles';
import type { RuleSet } from 'styled-components';
import { css, keyframes } from 'styled-components';

const { fonts, spacing } = theme;

export const checkboxWrapper = css`
  margin: 0 0 ${spacing.lg}px 0;

  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      margin: 0 0 ${spacing.sm}px 0;
    `,
  )}
`;

export const paragraphStyle = css`
  ${theme.responsiveText.BODY.SM};
  color: var(--neutral-black-70);
`;

export const wrapper = css`
  margin: 4rem 12rem;

  ${mediaQuery(
    {
      max: maxBreakpoints.M,
    },
    css`
      margin: 1rem;
    `,
  )}

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      margin: 0;
    `,
  )}
`;

export const header = css`
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  background-color: var(--brand-bluple-base);
  color: var(--neutral-white-base);
  padding: 2rem 1rem;
  ${theme.responsiveText.HEADING.LG};

  ${mediaQuery(
    { min: maxBreakpoints.S, max: maxBreakpoints.M },
    css`
      padding: 2rem;
    `,
  )}
  ${mediaQuery(
    { min: maxBreakpoints.M },
    css`
      padding: ${pxToRem(76)} 0 ${pxToRem(32)} ${pxToRem(208)};
    `,
  )}
`;

export const label = css`
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  font-weight: ${fonts.PRIMARY_SEMIBOLD.weight};
  color: var(--tertiary-blue-base);
  ${theme.responsiveText.BODY.MD};
  &:empty {
    display: none;
  }
`;

export const normalFontWeight = css`
  font-weight: ${fonts.PRIMARY_REGULAR.weight};
`;

export const pageSection = css`
  padding: 0 1rem 0.25rem;
`;

export const sectionHeading = css`
  color: var(--neutral-black-40);
  font-family: ${fonts.PRIMARY_REGULAR.family};
  ${theme.responsiveText.HEADING.XS};
`;

export const finalSectionHeading = css`
  color: var(--tertiary-blue-base);
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  ${theme.responsiveText.HEADING.SM}
`;

export const finalSectionText = css`
  color: var(--neutral-black-70);
  ${theme.responsiveText.HEADING.XS};
  margin: 0;
`;

export const sectionTitle = css`
  font-family: ${fonts.PRIMARY_SEMIBOLD.family};
  color: var(--neutral-black-40);
  ${theme.responsiveText.HEADING.MD};
  margin-top: 0;
  margin-bottom: 3.75rem;

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      margin-bottom: 2.5rem;
    `,
  )}
`;

export const subSection = css`
  margin-bottom: 3.75rem;

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      margin-bottom: 2.5rem;
    `,
  )}
`;

export const textFieldLabel = css`
  label {
    ${label}
  }
`;

export const dropdown = css`
  ${textFieldLabel}

  ${mediaQuery(
    {
      max: maxBreakpoints.M,
    },
    css`
      width: 100%;
    `,
  )}
`;

export const phoneNumberSection = css`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: inherit;
`;

export const compactGrid = css`
  grid-template-rows: 6rem;
`;

const appear = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const easeIn = css`
  animation: ${applyKeyframe(appear)} 0.5s ease-in;
  animation-fill-mode: forwards;
`;

export const grow = keyframes`
  from {
    opacity: 0;
    height: 0;
    transform: translateY(-50%);
  }

  to {
    opacity: 1;
    height: inherit;
    transform: translateY(0);
  }
`;

export const shrink = keyframes`
  from {
    opacity: 1;
    height: inherit;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    height: 0;
    transform: translateY(-50%);
  }
`;

export const growIn = css`
  animation: ${applyKeyframe(grow)} 0.5s ease-in;
  animation-fill-mode: forwards;
`;

export const businessAddress = css`
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

export const growOut = css`
  animation: ${applyKeyframe(shrink)} 0.5s ease-in;
  animation-fill-mode: forwards;
`;

export const textField = css`
  margin: 0 0 ${spacing.xl}px 0;
  max-width: 40rem;

  ${textFieldLabel}

  p {
    ${paragraphStyle};
  }
`;

export const descriptionPara = css`
  ${paragraphStyle};
`;

export const singleCheckboxWithLabel = css`
  ${subSection}

  > span {
    ${label}
  }
`;

export const socialMediaInputs = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      gap: 0.5rem;
      margin-bottom: 0;
    `,
  )}
`;

export const removableSocialMediaTextField = css`
  display: flex;
  grid-direction: row;
  align-items: end;
  gap: 1rem;

  input {
    width: 24rem;
  }
  button {
    margin-bottom: 0.5rem;
  }

  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      display: block;
      input {
        width: 100%;
      }
      button {
        float: right;
        margin-bottom: 0;
      }
    `,
  )}
`;

export const productTextFields = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      margin-bottom: 0;
    `,
  )}
`;

export const removableProductTextField = css`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  grid-gap: 0.5rem;
  align-items: inherit;

  button {
    margin-bottom: 0.5rem;
  }

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      grid-template-columns: 1fr;
      button {
        justify-self: end;
      }
    `,
  )}
`;

export const navigationButtonsGrid = css`
  display: grid;
  grid-template-columns: 14rem 1fr;
  margin: 3rem 0;

  button {
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.5;
  }

  button:nth-child(2) {
    grid-column-end: none;
  }

  ${mediaQuery(
    {
      max: maxBreakpoints.XS,
    },
    css`
      grid-template-columns: 1fr;

      && {
        button {
          width: 6rem;
          > * {
            width: 6rem;
          }
        }
      }
    `,
  )}
`;

export const grouped = css`
  margin: 2rem 0 2rem;
  padding: 0;

  > legend {
    ${label};
    margin-bottom: 1rem;
  }

  legend ~ p {
    margin-top: 0;
  }
`;

export const checkboxColumns = css`
  display: grid;
  grid-template-columns: 0.5fr 0.5fr;

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      grid-template-columns: 1fr;
      grid-template-rows: 2rem;
    `,
  )}
`;

export const onlineStoresColumns = css`
  display: grid;
  grid-template-columns: 20rem 19rem 2rem;
  grid-template-rows: 5rem;

  input {
    margin: 0;
  }

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      grid-template-columns: 1fr;
      grid-template-rows: 3rem;
      margin-bottom: 1rem;
    `,
  )}
`;

export const onlineStoreCheckbox = css`
  margin-top: 0.4rem;

  span {
    align-self: auto;
  }
`;

export const onlineStoreDeleteButton = css`
  margin-top: 0.4rem;

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      margin: 0.5rem 2.5rem 1rem;
    `,
  )}
`;

export const indentedCheckboxes = css`
  margin: 0 2.5rem;
`;

export const shortField = css`
  width: 18rem;

  ${textFieldLabel}

  ${mediaQuery(
    {
      max: maxBreakpoints.M,
    },
    css`
      width: 100%;
    `,
  )}
`;

export const webLinkField = css`
  margin-left: 2.5rem;

  label {
    display: none;
  }

  ${mediaQuery(
    { min: minBreakpoints.M },
    css`
      margin: 0 0 ${spacing.xl}px 0;
      width: 18rem;
    `,
  )}
`;

export const onlineStoreDialogue = css`
  ${indentedCheckboxes};

  padding: 0.75rem 0.75rem 1rem;
  border-radius: 1.25rem;
  border: solid 0.0625rem var(--neutral-black-30);
  margin-bottom: 2.5rem;
`;

export const onlineStoreGrid = css`
  display: grid;
  grid-gap: 0.5rem;
  grid-template-columns: 17rem 1fr 2rem;

  ${mediaQuery(
    { max: maxBreakpoints.S },
    css`
      grid-template-columns: 1fr;
    `,
  )}
`;

export const makeTwoColCheckboxGrid = (numberOfRows: number): RuleSet<object> => css`
  display: grid;
  grid-template-rows: repeat(${numberOfRows}, 2.5rem);
  gap: 10px;
  grid-auto-flow: column;
  grid-auto-columns: 1fr 1fr;

  ${mediaQuery(
    {
      max: maxBreakpoints.S,
    },
    css`
      display: block;
    `,
  )}
`;

export const submitButton = css`
  ${mediaQuery(
    { min: minBreakpoints.L },
    css`
      display: inline-block;
      width: ${pxToRem(159)};
    `,
  )}
`;

export const enterAddressManually = css`
  margin-left: -0.5rem;
`;

export const addButton = css`
  padding-left: 1rem;
  &::before {
    content: '+';
    font-size: 1.25rem;
    position: absolute;
    left: 0px;
    padding-right: 10px;
    top: 0.25rem;
  }
`;

const infoErrorField = css`
  border-radius: 0.625rem;
  display: flex;
  font-family: ${fonts.PRIMARY_REGULAR.family};
  font-weight: ${fonts.PRIMARY_REGULAR.weight};
`;

export const errorField = css`
  ${infoErrorField};
  animation: ${applyKeyframe(appear)} 0.25s ease-in-out;
  animation-fill-mode: forwards;
  background-color: var(--functional-red-50);
  color: var(--neutral-black-70);
  p {
    ${theme.responsiveText.BODY.SM};
    line-height: 1rem;
    align-self: center;
  }
`;

export const warningField = css`
  ${infoErrorField};
  background-color: var(--functional-yellow-30);
  color: var(--neutral-black-40);
  ${theme.responsiveText.BODY.MD};
`;

export const infoErrorIcon = css`
  width: 1rem;
  height: 1rem;
  min-width: 1rem;
  margin: 0.75rem;
`;

export const warningIcon = css`
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  margin: 0.75rem;
  align-self: initial;
  margin-top: 1rem;
`;

export const errorIcon = css`
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  margin: 0.75rem;
  align-self: center;
`;
